import { Observable } from "rxjs";
import HttpService from "./HttpService";
import { url } from "./APIEndPoints";
import {
  buildParamString,
  IAPICommonResponse,
  IAPIResponse,
  AuthService,
} from "@mindmemobile/mm-shared-global";
import {
  IAPICreateBusinessModel,
  IAPICreateMerchantAccountModel,
  IAPICreateMerchantModel,
  IAPIUpdateApplicationStateModel,
} from "../interfaces/IAPICreateMerchantModel";
import {
  IAPICreateBussinessModel,
  IAPICreateMerchantApplicationResponseModel,
  IAPIDeleteMerchantAccount,
  IAPIMerchantAccountModel,
  IAPIMerchantSetDefaultModel,
} from "../interfaces/IAPIMerchantModels";
import {
  IAPIMCCCodeResponseModel,
  IAPIMerchantAccountSummaryModel,
  IAPIAgencySummaryModel,
  IAPIAdminGridSummaryRequest,
} from "../interfaces";
import AdminAuthStore from "../store/AdminAuthStore";

class MerchantAccountService {
  private httpService: HttpService;

  constructor() {
    this.httpService = new HttpService(
      undefined,
      `${process.env.REACT_APP_API_KEY}`,
      process.env.REACT_APP_API_BASE_URL
    );
  }

  IsNMIUserNameAvailable(
    accountId: string,
    userName: string
  ): Observable<IAPIResponse<any>> {
    const request = {
      accountId: accountId,
      userName: userName,
    };

    return this.httpService.post<IAPIResponse<any>>(
      url.IsNMIUserNameAvailable,
      request
    );
  }

  createMerchantAccount(
    applicationData: IAPICreateMerchantAccountModel
  ): Observable<IAPIResponse<IAPICreateMerchantApplicationResponseModel>> {
    return this.httpService.post<
      IAPIResponse<IAPICreateMerchantApplicationResponseModel>
    >(url.createMerchantAccount, applicationData);
  }

  createBusiness(
    applicationData: IAPICreateBusinessModel
  ): Observable<IAPIResponse<IAPICreateBussinessModel>> {
    return this.httpService.post<IAPIResponse<IAPICreateBussinessModel>>(
      url.createBusiness,
      applicationData
    );
  }

  getBusinesses(accountId: string): Observable<IAPIResponse<any>> {
    const apiUrl = `${url.getBusinesses}?accountId=${accountId}`;
    return this.httpService.get<IAPIResponse<any>>(apiUrl);
  }

  getMerchantAccounts(
    accountId: string,
    pageNumber: number,
    pageSize: number,
    searchValue: string
  ): Observable<IAPIResponse<IAPIMerchantAccountModel[]>> {
    const params: string = buildParamString({
      accountId: accountId,
      pageNumber: pageNumber,
      pageSize: pageSize,
      searchValue: searchValue,
    });

    return this.httpService.get<IAPIResponse<IAPIMerchantAccountModel[]>>(
      url.getMerchantAccounts,
      params
    );
  }

  getCompleteApplicationLink(
    accountId: string,
    applicationId: string
  ): Observable<IAPIResponse<any>> {
    const params: string = buildParamString({
      accountId: accountId,
      applicationId: applicationId,
    });

    return this.httpService.get<IAPIResponse<any>>(
      url.getCompleteApplicationLink,
      params
    );
  }

  getIsValidBusiness(
    accountId: string,
    businessName: string
  ): Observable<IAPIResponse<any>> {
    const params: string = buildParamString({
      accountId: accountId,
      businessName: businessName,
    });

    return this.httpService.get<IAPIResponse<any>>(
      url.getIsValidBusiness,
      params
    );
  }

  updateApplicationStatus(
    data: IAPIUpdateApplicationStateModel
  ): Observable<IAPIResponse<IAPICommonResponse>> {
    return this.httpService.put<IAPIResponse<IAPICommonResponse>>(
      url.updateMerchantApplicationStatus,
      data
    );
  }

  deleteMerchantAccount(
    data: IAPIDeleteMerchantAccount
  ): Observable<IAPIResponse<IAPICommonResponse>> {
    return this.httpService.delete<IAPIResponse<IAPICommonResponse>>(
      url.deleteMerchantAccount,
      data
    );
  }

  cancelMerchantAccount(
    data: IAPIDeleteMerchantAccount
  ): Observable<IAPIResponse<IAPICommonResponse>> {
    return this.httpService.put<IAPIResponse<IAPICommonResponse>>(
      url.cancelMerchantAccount,
      data
    );
  }

  // Admin/bulk use case: merchant accounts across ALL accounts in one call
  // (lightweight name/gateway/status projection), paged and filtered server-side.
  // AdminApiKey is a static shared secret forwarded from Main_Web2's admin-only build via
  // postMessage - it proves this call came from the trusted admin embed, since the regular
  // MindMeKey (already attached by httpService) is shipped in every visitor's bundle.
  getMerchantAccountsSummary(
    request: IAPIAdminGridSummaryRequest
  ): Observable<IAPIResponse<IAPIMerchantAccountSummaryModel[]>> {
    return this.httpService.post<IAPIResponse<IAPIMerchantAccountSummaryModel[]>>(
      url.getMerchantAccountsSummary,
      request,
      { headers: { AdminApiKey: AdminAuthStore.adminApiKey } }
    );
  }

  // Admin use case: full agency list (id/name/branding/status) - populates the admin
  // grid's agency filter dropdown.
  getMerchantAccountAgencies(): Observable<
    IAPIResponse<IAPIAgencySummaryModel[]>
  > {
    return this.httpService.post<IAPIResponse<IAPIAgencySummaryModel[]>>(
      url.getAgencies,
      {},
      { headers: { AdminApiKey: AdminAuthStore.adminApiKey } }
    );
  }

  getFiservMCCS(): Observable<IAPIResponse<IAPIMCCCodeResponseModel[]>> {
    return this.httpService.get<IAPIResponse<IAPIMCCCodeResponseModel[]>>(
      url.getFiservMCCS,
      undefined
    );
  }

  setDefaultMerchantAccount(
    accountId: string,
    merchantAccountId: string
  ): Observable<IAPIResponse<IAPICommonResponse>> {
    const data: IAPIMerchantSetDefaultModel = {
      accountId: accountId,
      id: merchantAccountId,
      userId: AuthService.mindmeAccountDetails?.userId || "",
    };

    return this.httpService.put<IAPIResponse<IAPICommonResponse>>(
      url.setDefaultMerchantAccount,
      data
    );
  }
}

export default new MerchantAccountService();
